import React, { useRef } from 'react';
import { usePanelDistribution } from '@/components/pages/kiosk/monitor/hooks/usePanelDistribution';
import pacuEmpty from '../../../../assets/images/postoperation.svg';
import preopEmpty from '../../../../assets/images/preop.svg';
import waitingRoomEmpty from '../../../../assets/images/waitingroom.svg';
import { PACU, POST_OP, PRE_OP } from '../../../entities/room/enums';
import { INTERNAL_MONITOR } from '../../../entities/screens/enums';
import PanelSlots from '../../../Panel/PanelSlots';
import { PanelsStatus } from './__DEPRECATED__Monitor';
import RoomPanelSlots from './RoomPanelSlots';
import { useScreenConfiguration } from '@/contexts/ScreenConfigurationContext';

const BottomPanelStatus = ({
  isMini,
  waitingRoom,
  preOp,
  pacu,
  postOps,
  hasPostop,
  hasPreOpPriorityModule,
  hasNoteModule,
  messagingView,
  carouselSize,
  staffMode,
  showLatePreOpIndicator,
  isSuperAdmin,
  getStaffForRoom,
  showStaffTracking,
  isGroupAdmin,
  topRowHeight,
  isKiosk = true,
}) => {
  // This can change depending on what number of panels are needed, for now it is static as we do not have a need for dynamic changes
  const CAROUSEL_SIZE_PREOP_FOCUSED = 7;
  const PREOP_PANEL_NUMBER = 3;
  const PACU_PANEL_NUMBER = 3;
  const WAITING_ROOM_PANEL_NUMBER = 2;
  const POSTOP_ROOM_PANEL_NUMBER = 2;

  const { preOpFocused, pacuFocused, showPatientNotesOnInternalScreen } = useScreenConfiguration();
  const containerRef = useRef(null);
  const focusInputs = preOpFocused
    ? {
        waitingRoom,
        preOp,
        pacu: undefined,
        postOps: undefined,
        hasPostop: false,
      }
    : pacuFocused
      ? {
          waitingRoom: undefined,
          preOp: undefined,
          pacu,
          postOps,
          hasPostop,
        }
      : {
          waitingRoom,
          preOp,
          pacu,
          postOps,
          hasPostop,
        };

  const { wrPanels, preOpPanels, MAX_PANELS, actualCarouselSize } = usePanelDistribution({
    containerRef,
    waitingRoom: focusInputs.waitingRoom,
    preOp: focusInputs.preOp,
    pacu: focusInputs.pacu,
    postOps: focusInputs.postOps,
    hasPostop: focusInputs.hasPostop,
    topRowHeight,
    isKiosk,
  });

  const focusedCarouselSize = Math.min(actualCarouselSize, CAROUSEL_SIZE_PREOP_FOCUSED);

  if (preOpFocused) {
    return (
      <PanelsStatus ref={containerRef} $miniView={isMini} $moreSpace={messagingView}>
        {waitingRoom && waitingRoom.id && (
          <RoomPanelSlots
            room={waitingRoom}
            focused={true}
            screenType={INTERNAL_MONITOR}
            panelNumber={waitingRoom.patients.length === 0 ? 1 : WAITING_ROOM_PANEL_NUMBER}
            carouselSize={focusedCarouselSize}
            imageEmpty={waitingRoomEmpty}
            messagingView={messagingView}
            staffMode={staffMode}
            showPatientNotesOnInternalScreen={showPatientNotesOnInternalScreen}
            isSuperAdmin={isSuperAdmin}
            getStaffForRoom={getStaffForRoom}
            showStaffTracking={showStaffTracking}
            isGroupAdmin={isGroupAdmin}
            isKiosk={isKiosk}
          />
        )}
        {preOp && preOp.id && (
          <RoomPanelSlots
            room={preOp}
            focused={true}
            roomType={PRE_OP}
            screenType={INTERNAL_MONITOR}
            panelNumber={PREOP_PANEL_NUMBER}
            carouselSize={focusedCarouselSize}
            imageEmpty={preopEmpty}
            hasPreOpPriorityModule={hasPreOpPriorityModule}
            hasNoteModule={hasNoteModule}
            messagingView={messagingView}
            staffMode={staffMode}
            showLatePreOpIndicator={showLatePreOpIndicator}
            isSuperAdmin={isSuperAdmin}
            getStaffForRoom={getStaffForRoom}
            showStaffTracking={showStaffTracking}
            isGroupAdmin={isGroupAdmin}
            isKiosk={isKiosk}
          />
        )}
      </PanelsStatus>
    );
  }

  if (pacuFocused) {
    return (
      <PanelsStatus ref={containerRef} $miniView={isMini} $moreSpace={messagingView}>
        <>
          {pacu && pacu.id && (
            <RoomPanelSlots
              room={pacu}
              name={pacu?.name}
              roomType={PACU}
              focused={true}
              screenType={INTERNAL_MONITOR}
              panelNumber={PACU_PANEL_NUMBER}
              carouselSize={focusedCarouselSize}
              imageEmpty={pacuEmpty}
              highlightReady
              patientsCount={pacu?.patients?.length || 0}
              showEmptyContent
              showPacuNote={hasNoteModule}
              showBed={true}
              messagingView={messagingView}
              staffMode={staffMode}
              isSuperAdmin={isSuperAdmin}
              getStaffForRoom={getStaffForRoom}
              showStaffTracking={showStaffTracking}
              isGroupAdmin={isGroupAdmin}
              isKiosk={isKiosk}
            />
          )}
          {hasPostop &&
            postOps &&
            Array.isArray(postOps) &&
            postOps
              .filter(postOp => postOp.id)
              .map(postOp => (
                <RoomPanelSlots
                  key={postOp.id}
                  room={postOp}
                  focused={true}
                  roomType={POST_OP}
                  panelNumber={POSTOP_ROOM_PANEL_NUMBER}
                  carouselSize={focusedCarouselSize}
                  screenType={INTERNAL_MONITOR}
                  name={postOp?.name}
                  patientsCount={postOp?.patients?.length || 0}
                  imageEmpty={pacuEmpty}
                  showBed={true}
                  highlightReady
                  showEmptyContent
                  messagingView={messagingView}
                  staffMode={staffMode}
                  isSuperAdmin={isSuperAdmin}
                  getStaffForRoom={getStaffForRoom}
                  showStaffTracking={showStaffTracking}
                  isGroupAdmin={isGroupAdmin}
                  isKiosk={isKiosk}
                />
              ))}
        </>
      </PanelsStatus>
    );
  }

  // Ensure we don't stack vertically if we decided to use multiple panels (overriding legacy responsive styles)
  const containerStyle = MAX_PANELS > 1 ? { flexFlow: 'row' } : undefined;

  return (
    <PanelsStatus ref={containerRef} $miniView={isMini} $moreSpace={messagingView} style={containerStyle}>
      <>
        {waitingRoom && waitingRoom.id && (
          <RoomPanelSlots
            room={waitingRoom}
            focused={false}
            screenType={INTERNAL_MONITOR}
            panelNumber={wrPanels}
            carouselSize={actualCarouselSize}
            imageEmpty={waitingRoomEmpty}
            messagingView={messagingView}
            staffMode={staffMode}
            showPatientNotesOnInternalScreen={showPatientNotesOnInternalScreen}
            isSuperAdmin={isSuperAdmin}
            getStaffForRoom={getStaffForRoom}
            showStaffTracking={showStaffTracking}
            isGroupAdmin={isGroupAdmin}
            isKiosk={isKiosk}
          />
        )}
        {preOp && preOp.id && (
          <RoomPanelSlots
            room={preOp}
            focused={false}
            roomType={PRE_OP}
            screenType={INTERNAL_MONITOR}
            panelNumber={preOpPanels}
            carouselSize={actualCarouselSize}
            imageEmpty={preopEmpty}
            hasPreOpPriorityModule={hasPreOpPriorityModule}
            hasNoteModule={hasNoteModule}
            messagingView={messagingView}
            staffMode={staffMode}
            showLatePreOpIndicator={showLatePreOpIndicator}
            isSuperAdmin={isSuperAdmin}
            getStaffForRoom={getStaffForRoom}
            showStaffTracking={showStaffTracking}
            isGroupAdmin={isGroupAdmin}
            isKiosk={isKiosk}
          />
        )}
      </>
      <>
        {pacu && pacu.id && (
          <PanelSlots
            {...pacu}
            name={pacu?.name}
            roomType={PACU}
            patientsCount={pacu?.patients?.length || 0}
            screenType={INTERNAL_MONITOR}
            image={pacuEmpty}
            showBed={true}
            highlightReady
            showEmptyContent
            showPacuNote={hasNoteModule}
            messagingView={messagingView}
            carouselSize={actualCarouselSize}
            staffMode={staffMode}
            isSuperAdmin={isSuperAdmin}
            roomStaff={showStaffTracking && getStaffForRoom && pacu.id ? getStaffForRoom(pacu.id) : []}
            isGroupAdmin={isGroupAdmin}
            isKiosk={isKiosk}
          />
        )}

        {hasPostop &&
          postOps &&
          Array.isArray(postOps) &&
          postOps
            .filter(postOp => postOp.id)
            .map(postOp => (
              <PanelSlots
                key={postOp.id}
                {...postOp}
                name={postOp?.name}
                patientsCount={postOp?.patients?.length || 0}
                roomType={POST_OP}
                screenType={INTERNAL_MONITOR}
                image={pacuEmpty}
                showBed={true}
                highlightReady
                showEmptyContent
                messagingView={messagingView}
                carouselSize={actualCarouselSize}
                staffMode={staffMode}
                isSuperAdmin={isSuperAdmin}
                roomStaff={showStaffTracking && getStaffForRoom && postOp.id ? getStaffForRoom(postOp.id) : []}
                isGroupAdmin={isGroupAdmin}
                isKiosk={isKiosk}
              />
            ))}
      </>
    </PanelsStatus>
  );
};

export default BottomPanelStatus;
